import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { loadGsap } from './gsap-loader.js';
import { addToWall, readWall, WALL_EVENT, MAX_ITEMS } from './wall-store.js';

const DIMENSIONS = /(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i;
const VIEWS = ['art', 'scale'];
const FINISHES = ['black', 'white', 'brown'];
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const VIEW_NAMES = {
  art: 'Artwork view',
  detail: 'Frame detail',
  scale: 'Living room perspective',
};

/**
 * Product page with live variant selection and three preview views, one of which
 * scales the print against a gallery wall using its real dimensions in inches.
 */
class ProductStudioComponent extends HTMLElement {
  #abort = new AbortController();
  /** @type {any} */
  #gsap = null;
  #reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  #optionNames = [];
  #variants = [];
  #selected = [];
  #view = 'art';
  #adding = false;
  #resizeObserver = null;
  #compose = false;
  #productInfo = null;

  connectedCallback() {
    try {
      const data = JSON.parse(this.querySelector('[data-variants]')?.textContent || '{}');
      this.#optionNames = data.options ?? [];
      this.#variants = data.variants ?? [];
      this.#productInfo = data.product ?? null;
    } catch {
      return;
    }

    const q = (selector) => this.querySelector(selector);
    this.els = {
      stage: q('[data-stage]'),
      zoomLayer: q('[data-zoom-layer]'),
      zoomLabel: q('[data-zoom-label]'),
      art: q('[data-art]'),
      measure: q('[data-measure]'),
      caption: q('[data-view-caption]'),
      meta: q('[data-preview-meta]'),
      price: q('[data-price]'),
      variantId: q('[data-variant-id]'),
      form: q('[data-product-form]'),
      add: q('[data-add]'),
      addLabel: q('[data-add-label]'),
      error: q('[data-error]'),
      guide: q('[data-guide]'),
      wallAdd: q('[data-wall-add]'),
      wallAddLabel: q('[data-wall-add-label]'),
      wallView: q('[data-wall-view]'),
      wallCount: q('[data-wall-count]'),
      frame: q('[data-frame]'),
      mat: q('[data-mat]'),
    };
    this.#compose = this.dataset.compose === 'true' && Boolean(this.els.frame);
    this.optionButtons = Array.from(this.querySelectorAll('.ps__option'));
    this.thumbs = Array.from(this.querySelectorAll('.ps__thumb'));
    this.configs = Array.from(this.querySelectorAll('.ps__config'));

    this.#selected = this.#optionNames.map((_, index) => {
      const pressed = this.optionButtons.find(
        (button) => Number(button.dataset.optionIndex) === index && button.getAttribute('aria-pressed') === 'true'
      );
      return pressed?.dataset.value ?? '';
    });

    this.#bind();
    this.#sync(false);

    loadGsap().then((gsap) => {
      if (!this.isConnected) return;
      this.#gsap = gsap;
      this.#layout(false);
      this.#playHandoff();
    });

    // Never leave the page covered if GSAP is slow or blocked.
    if (window.__psHandoff) setTimeout(() => this.#endHandoff(), 4000);
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#resizeObserver?.disconnect();
    this.#gsap?.killTweensOf(this.els.art);
  }

  #bind() {
    const { signal } = this.#abort;
    const on = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { signal, ...options });

    this.optionButtons.forEach((button) =>
      on(button, 'click', () => {
        this.#selected[Number(button.dataset.optionIndex)] = button.dataset.value;
        this.#sync(true);
      })
    );
    this.thumbs.forEach((thumb) => on(thumb, 'click', () => this.#setView(thumb.dataset.view)));
    on(this.querySelector('[data-stage-prev]'), 'click', () => this.#cycle(-1));
    on(this.querySelector('[data-stage-next]'), 'click', () => this.#cycle(1));
    on(this.els.form, 'submit', (event) => {
      event.preventDefault();
      this.#addToBag();
    });

    this.#bindGuide(on);
    this.#bindWall(on);
    this.#bindAccordions(on);
    this.#bindSummary(on);

    this.#resizeObserver = new ResizeObserver(() => this.#layout(false));
    this.#resizeObserver.observe(this.els.stage);

    this.#bindZoom(on);
    this.#bindSpace(on);
  }

  /* ---------- summary ---------- */

  /** The short intro is cut at 200 characters; See more swaps in the whole text and back. */
  #bindSummary(on) {
    const text = this.querySelector('[data-summary-text]');
    const toggle = this.querySelector('[data-summary-toggle]');
    if (!text || !toggle) return;
    on(toggle, 'click', () => {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      text.textContent = open ? text.dataset.full : text.dataset.short;
      toggle.textContent = open ? 'See less' : 'See more';
      toggle.setAttribute('aria-expanded', String(open));
    });
  }

  /* ---------- accordions ---------- */

  /** Native <details> snap open; this slides the panel instead. Falls back to the native toggle without GSAP. */
  #bindAccordions(on) {
    this.querySelectorAll('.ps__accordion').forEach((details) => {
      const summary = details.querySelector('summary');
      const body = details.querySelector('.ps__rte');
      if (!summary || !body) return;

      on(summary, 'click', (event) => {
        event.preventDefault();
        const gsap = this.#gsap;
        if (!gsap || this.#reduceMotion) {
          details.open = !details.open;
          return;
        }

        gsap.killTweensOf(body);
        const reset = () => gsap.set(body, { clearProps: 'height,opacity' });

        if (!details.open) {
          details.classList.remove('is-closing');
          details.open = true;
          gsap.fromTo(
            body,
            { height: 0, opacity: 0 },
            { height: 'auto', opacity: 1, duration: 0.6, ease: 'expo.out', onComplete: reset }
          );
        } else {
          details.classList.add('is-closing');
          gsap.to(body, {
            height: 0,
            opacity: 0,
            duration: 0.4,
            ease: 'power3.inOut',
            onComplete: () => {
              details.open = false;
              details.classList.remove('is-closing');
              reset();
            },
          });
        }
      });
    });
  }

  /* ---------- gallery wall ---------- */

  #bindWall(on) {
    if (!this.els.wallAdd) return;
    on(this.els.wallAdd, 'click', () => this.#addToWall());
    on(window, WALL_EVENT, () => this.#syncWall());
    on(window, 'storage', (event) => {
      if (event.key === 'melonary:wall') this.#syncWall();
    });
    this.#syncWall();
  }

  #syncWall() {
    const count = readWall().items.length;
    this.els.wallCount.textContent = `(${count})`;
    this.els.wallView.hidden = count === 0;
  }

  /** Adds exactly what is selected right now (size, frame, border) to the visitor's wall. */
  #addToWall() {
    const variant = this.#findVariant();
    const info = this.#productInfo;
    const { wallAdd, wallAddLabel } = this.els;
    if (!variant || !info) return;

    let label = 'Added to wall ✓';
    if (!variant.available) {
      label = 'Sold out';
    } else {
      const result = addToWall(
        {
          ...info,
          optionNames: this.#optionNames,
          variants: this.#variants.map(({ id, options, cents, text, available }) => ({ id, options, cents, text, available })),
        },
        this.#selected
      );
      if (!result.ok) label = `Wall is full (${MAX_ITEMS})`;
    }

    wallAddLabel.textContent = label;
    wallAdd.disabled = true;
    clearTimeout(this.wallTimer);
    this.wallTimer = setTimeout(() => {
      wallAddLabel.textContent = 'Add to wall';
      wallAdd.disabled = false;
    }, 1800);
  }

  /* ---------- arriving from the wall ---------- */

  #endHandoff() {
    document.getElementById('ps-handoff')?.remove();
    document.documentElement.removeAttribute('data-ps-handoff');
    window.__psHandoff = null;
  }

  /** Eases the frame left at full size by the wall into its resting place in the preview. */
  async #playHandoff() {
    const handoff = window.__psHandoff;
    const layer = document.getElementById('ps-handoff');
    const gsap = this.#gsap;
    if (!handoff || !layer) return;
    if (!gsap || !this.els.frame) return this.#endHandoff();

    // The real frame is revealed at the end, so its image must be decoded by then. Decode it while the frame is
    // already moving, instead of holding the animation back until it is ready.
    const realPrint = this.els.frame.querySelector('img');
    const decoded = realPrint?.decode
      ? Promise.race([realPrint.decode().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 800))])
      : Promise.resolve();
    if (!document.getElementById('ps-handoff')) return;

    const lead = layer.firstElementChild;
    const leadStyle = getComputedStyle(lead);
    const current = lead.getBoundingClientRect();
    const from = {
      left: current.left,
      top: current.top,
      width: current.width,
      height: current.height,
      border: parseFloat(leadStyle.borderTopWidth) * handoff.scale,
      padding: parseFloat(leadStyle.paddingTop) * handoff.scale,
    };

    // Swap the transform for plain geometry so size, border and mat can all animate together.
    gsap.set(lead, {
      clearProps: 'transform',
      left: from.left,
      top: from.top,
      width: from.width,
      height: from.height,
      borderWidth: from.border,
      padding: from.padding,
    });

    // Aim at the real frame's live position every tick, so a late layout shift can't cause a jump at the end.
    const progress = { value: 0 };
    const lerp = (a, b, t) => a + (b - a) * t;
    const update = () => {
      const target = this.els.frame.getBoundingClientRect();
      const frameStyle = getComputedStyle(this.els.frame);
      const matStyle = getComputedStyle(this.els.mat);
      const t = progress.value;
      gsap.set(lead, {
        left: lerp(from.left, target.left, t),
        top: lerp(from.top, target.top, t),
        width: lerp(from.width, target.width, t),
        height: lerp(from.height, target.height, t),
        borderWidth: lerp(from.border, parseFloat(frameStyle.paddingTop), t),
        padding: lerp(from.padding, parseFloat(matStyle.paddingTop), t),
      });
    };

    // The rest of the page (header, details, everything but the flying frame) fades in while the frame settles.
    const page = [...document.body.children].filter((el) => el.id !== 'ps-handoff' && !/^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(el.tagName));

    gsap
      .timeline({ onComplete: () => decoded.then(() => this.#endHandoff()) })
      .to(page, { opacity: 1, duration: 0.9, ease: 'power2.inOut', clearProps: 'opacity' }, 0.2)
      .to(progress, { value: 1, duration: 1, ease: 'expo.inOut', onUpdate: update }, 0)
      .to(lead, { boxShadow: getComputedStyle(this.els.frame).boxShadow, duration: 1, ease: 'expo.inOut' }, 0)
      .to(layer, { backgroundColor: 'rgba(0,0,0,0)', duration: 0.7, ease: 'power2.inOut' }, 0.2);
  }

  /* ---------- variants ---------- */

  #findVariant(selection = this.#selected) {
    return this.#variants.find((variant) => variant.options.every((value, index) => value === selection[index]));
  }

  /** Short human label for an option value, e.g. "A4 (9x13 inches)" -> "A4". */
  #short(value, optionIndex) {
    const name = (this.#optionNames[optionIndex] ?? '').toLowerCase();
    const lower = value.toLowerCase();
    if (/border|trim/.test(name)) return lower === 'yes' ? 'Border' : lower === 'no' ? 'No border' : value;
    return value.split(' (')[0];
  }

  /** The selected frame finish, read from the option named "Frame". */
  #finish() {
    const index = this.#optionNames.findIndex((name) => /frame/i.test(name));
    const value = (this.#selected[index] ?? '').toLowerCase();
    return FINISHES.includes(value) ? value : 'black';
  }

  /** True when the "Trim/Border" option is set to No. */
  #noTrim() {
    const index = this.#optionNames.findIndex((name) => /border|trim/i.test(name));
    return (this.#selected[index] ?? '').toLowerCase() === 'no';
  }

  #dimensions(variant = this.#findVariant()) {
    for (const value of variant?.options ?? []) {
      const match = value.match(DIMENSIONS);
      if (match) return { w: Number(match[1]), h: Number(match[2]), text: `${match[1]}×${match[2]} inches` };
    }
    return null;
  }

  #sync(animate) {
    const variant = this.#findVariant();
    const els = this.els;

    this.optionButtons.forEach((button) => {
      const index = Number(button.dataset.optionIndex);
      const isSelected = this.#selected[index] === button.dataset.value;
      button.setAttribute('aria-pressed', String(isSelected));

      const test = [...this.#selected];
      test[index] = button.dataset.value;
      const candidate = this.#findVariant(test);
      button.classList.toggle('is-unavailable', !candidate?.available);
    });

    if (variant) {
      els.variantId.value = String(variant.id);
      els.price.textContent = variant.price;
      els.add.disabled = !variant.available || this.#adding;
      els.addLabel.textContent = variant.available ? 'ADD TO BAG' : 'SOLD OUT';

      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url);
    } else {
      els.add.disabled = true;
      els.addLabel.textContent = 'UNAVAILABLE';
    }

    const shortLabels = this.#selected.map((value, index) => this.#short(value, index));
    els.meta.textContent = shortLabels.join(' / ');
    els.error.hidden = true;

    this.#layout(animate);
  }

  /* ---------- preview ---------- */

  #setView(view) {
    if (!VIEWS.includes(view)) return;
    this.#view = view;
    this.#resetZoom(false);
    this.els.stage.dataset.view = view;
    this.els.caption.textContent = `0${VIEWS.indexOf(view) + 1} / ${VIEW_NAMES[view]}`;
    this.thumbs.forEach((thumb) => {
      const active = thumb.dataset.view === view;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', String(active));
    });
    this.#layout(true);
  }

  #cycle(step) {
    this.#setView(VIEWS[(VIEWS.indexOf(this.#view) + step + VIEWS.length) % VIEWS.length]);
  }

  /** Sizes the mockup from the selected dimensions and positions it for the active view. */
  #layout(animate) {
    const { stage, art, measure } = this.els;
    const W = stage.clientWidth;
    const H = stage.clientHeight;
    const img = art.querySelector('img');
    if (!W || !H || !img) return;

    const dims = this.#dimensions();
    const fill = Math.max(0.3, (Number(this.dataset.frameFill) || 50) / 100);
    if (this.#compose && dims) {
      this.#layoutComposed(animate, dims, W, H);
      return;
    }

    const base = Math.round(Math.min(W * 0.8, 720));
    art.style.setProperty('--ps-art-base', `${base}px`);

    let boxWidth = base * 0.6;
    if (dims) {
      let printWidth;
      if (this.#view === 'scale') {
        // The room photo's sofa is treated as 84in wide, filling about 54% of the stage.
        const pxPerInch = Math.min((W * 0.54) / 84, (H * 0.44) / 32, Math.max(280, H - 160) / 42);
        printWidth = dims.w * pxPerInch;
      } else {
        const areas = this.#variants.map((variant) => this.#dimensions(variant)).filter(Boolean);
        const smallest = Math.min(...areas.map((d) => d.w * d.h));
        const factor = Math.sqrt((dims.w * dims.h) / smallest);
        // Compressed so every size stays on screen, but still clearly ordered.
        printWidth = Math.min(190 * Math.pow(factor, 0.62), W * 0.55, (H * 0.78 * dims.w) / dims.h);
      }
      boxWidth = printWidth / fill;
      measure.textContent = `${dims.w} × ${dims.h} INCHES`;
    } else {
      measure.textContent = '';
    }
    // The product image is a card around the print; only the print needs to stay on screen.
    boxWidth = Math.min(boxWidth, base * 2.5);

    let scale = boxWidth / base;
    let y = 0;
    if (this.#view === 'detail') scale *= 1.6;
    else if (this.#view === 'scale') y = -0.18 * H;
    else if (this.#view === 'room' && !stage.hasAttribute('data-has-room')) {
      scale *= 0.76;
      y = -27;
    }

    if (this.#gsap) {
      this.#gsap.to(art, {
        scale,
        x: 0,
        y,
        xPercent: -50,
        yPercent: -50,
        duration: animate && !this.#reduceMotion ? 0.7 : 0,
        ease: 'expo.out',
        overwrite: 'auto',
      });
    } else {
      art.style.transform = `translate(-50%, -50%) translateY(${y}px) scale(${scale})`;
    }
  }

  /** Draws the frame at the selected size, finish and border, then positions it for the active view. */
  #layoutComposed(animate, dims, W, H) {
    const { art, measure, frame, mat } = this.els;
    const areas = this.#variants.map((variant) => this.#dimensions(variant)).filter(Boolean);
    const smallest = Math.min(...areas.map((d) => d.w * d.h));
    const factor = Math.sqrt((dims.w * dims.h) / smallest);
    const scaleView = this.#view === 'scale';

    let outerW;
    if (scaleView) {
      // The room photo's sofa is treated as 84in wide, filling about 54% of the stage.
      outerW = dims.w * Math.min((W * 0.54) / 84, (H * 0.44) / 32, Math.max(280, H - 160) / 42);
    } else {
      // Compressed so every size stays on screen, but still clearly ordered.
      outerW = Math.min(170 * Math.pow(factor, 0.55), W * 0.5, (H * 0.78 * dims.w) / dims.h);
    }
    const noTrim = this.#noTrim();
    // The frame's own edge is the same width at every size. In the room it follows the room's scale, so A4 and A1
    // frames have the same moulding; elsewhere it is the site-wide width.
    const edge = scaleView
      ? clamp((outerW / dims.w) * 0.7, 1.2, 6)
      : parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--frame-border')) || 7;
    const matSize = scaleView ? clamp(outerW * 0.048, 1.5, 11) : clamp(outerW * 0.07, 5, 19);

    // Shape the frame so the space inside its edge and mat has exactly the artwork's own shape. The paper size alone
    // would leave that space a slightly different shape, and the picture would be trimmed at the top and bottom,
    // which shows most when there is no mat to absorb the difference.
    const print = frame.querySelector('img');
    const artRatio = print ? (print.naturalWidth || Number(print.getAttribute('width'))) / (print.naturalHeight || Number(print.getAttribute('height'))) : 0;
    const inset = 2 * (edge + (noTrim ? 0 : matSize));
    const outerH = artRatio > 0 ? (outerW - inset) / artRatio + inset : (outerW * dims.h) / dims.w;

    frame.dataset.finish = this.#finish();
    mat.classList.toggle('no-trim', noTrim);
    art.style.setProperty('--ps-frame-pad', scaleView ? `${edge}px` : 'var(--frame-border)');
    art.style.setProperty('--ps-mat-pad', `${matSize}px`);
    this.els.measure.textContent = `${dims.w} × ${dims.h} INCHES`;
    measure.hidden = false;

    let scale = 1;
    let y = 0;
    // A close-up: zoom in well past the normal view, but stop once the frame fills the stage.
    if (this.#view === 'detail') scale = Math.max(1, Math.min(1.7, W / outerW, H / outerH));
    else if (scaleView) y = -0.18 * H;
    else if (this.#view === 'room') {
      scale = 0.76;
      y = -27;
    }

    if (this.#gsap) {
      this.#gsap.to(art, {
        width: outerW,
        height: outerH,
        scale,
        x: 0,
        y,
        xPercent: -50,
        yPercent: -50,
        duration: animate && !this.#reduceMotion ? 0.6 : 0,
        ease: 'expo.out',
        overwrite: 'auto',
      });
    } else {
      art.style.width = `${outerW}px`;
      art.style.height = `${outerH}px`;
      art.style.transform = `translate(-50%, -50%) translateY(${y}px) scale(${scale})`;
    }

  }

  /* ---------- zoom (living room view) ---------- */

  #zoom = { scale: 1, x: 0, y: 0 };
  #pointers = new Map();
  #pinch = null;

  /** Pinch, drag and double-tap to look closely at the room. Only the living room view zooms. */
  #bindZoom(on) {
    const { stage, zoomLayer } = this.els;
    if (!zoomLayer) return;
    let drag = null;
    let lastTap = 0;

    // On a touch screen the browser would take a two-finger pinch (and a quick double tap) to zoom the whole page. In
    // the living room those gestures belong to the picture, so stop the browser's own versions. Safari needs the
    // gesture events stopped as well as the touch events.
    const keepForPicture = (event) => {
      if (this.#view === 'scale') event.preventDefault();
    };
    on(stage, 'touchmove', (event) => event.touches.length > 1 && keepForPicture(event), { passive: false });
    ['gesturestart', 'gesturechange', 'gestureend'].forEach((type) => on(stage, type, keepForPicture, { passive: false }));
    on(stage, 'dblclick', keepForPicture);

    on(stage, 'pointerdown', (event) => {
      if (this.#view !== 'scale' || event.target.closest('button')) return;
      this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.#pointers.size === 2) {
        const [a, b] = [...this.#pointers.values()];
        this.#pinch = { distance: Math.hypot(a.x - b.x, a.y - b.y), scale: this.#zoom.scale };
        drag = null;
      } else if (this.#zoom.scale > 1) {
        drag = { x: event.clientX, y: event.clientY, startX: this.#zoom.x, startY: this.#zoom.y };
        stage.setPointerCapture(event.pointerId);
      }
      const now = performance.now();
      if (this.#pointers.size === 1 && now - lastTap < 300) {
        this.#zoomTo(this.#zoom.scale > 1 ? 1 : 2.4, event.clientX, event.clientY, true);
        lastTap = 0;
      } else {
        lastTap = now;
      }
    });

    on(stage, 'pointermove', (event) => {
      if (!this.#pointers.has(event.pointerId)) return;
      this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.#pinch && this.#pointers.size === 2) {
        const [a, b] = [...this.#pointers.values()];
        const next = clamp(this.#pinch.scale * (Math.hypot(a.x - b.x, a.y - b.y) / this.#pinch.distance), 1, 4);
        this.#zoomTo(next, (a.x + b.x) / 2, (a.y + b.y) / 2, false);
      } else if (drag) {
        this.#zoom.x = drag.startX + (event.clientX - drag.x);
        this.#zoom.y = drag.startY + (event.clientY - drag.y);
        this.#applyZoom(false);
      }
    });

    const end = (event) => {
      this.#pointers.delete(event.pointerId);
      if (this.#pointers.size < 2) this.#pinch = null;
      if (!this.#pointers.size) drag = null;
    };
    on(stage, 'pointerup', end);
    on(stage, 'pointercancel', end);

    on(this.querySelector('[data-zoom]'), 'click', () => {
      if (this.#view !== 'scale') this.#setView('scale');
      const { scale } = this.#zoom;
      const rect = stage.getBoundingClientRect();
      this.#zoomTo(scale >= 2.4 ? 1 : scale >= 1.6 ? 2.4 : 1.6, rect.left + rect.width / 2, rect.top + rect.height / 2, true);
    });
  }

  /** Zooms about a screen point, keeping that point under the finger. */
  #zoomTo(next, clientX, clientY, animate) {
    const rect = this.els.stage.getBoundingClientRect();
    const { scale, x, y } = this.#zoom;
    const px = clientX - rect.left - rect.width / 2;
    const py = clientY - rect.top - rect.height / 2;
    const ratio = next / scale;
    this.#zoom.scale = next;
    this.#zoom.x = px - (px - x) * ratio;
    this.#zoom.y = py - (py - y) * ratio;
    this.#applyZoom(animate);
  }

  #applyZoom(animate) {
    const { stage, zoomLayer, zoomLabel } = this.els;
    const { scale } = this.#zoom;
    const maxX = (stage.clientWidth * (scale - 1)) / 2;
    const maxY = (stage.clientHeight * (scale - 1)) / 2;
    this.#zoom.x = clamp(this.#zoom.x, -maxX, maxX);
    this.#zoom.y = clamp(this.#zoom.y, -maxY, maxY);
    stage.classList.toggle('is-zoomed', scale > 1.01);
    if (zoomLabel) zoomLabel.textContent = `${Math.round(scale * 10) / 10}×`;
    const target = { scale, x: this.#zoom.x, y: this.#zoom.y };
    if (this.#gsap) {
      this.#gsap.to(zoomLayer, {
        ...target,
        duration: animate && !this.#reduceMotion ? 0.5 : 0,
        ease: 'expo.out',
        overwrite: 'auto',
      });
    } else {
      zoomLayer.style.transform = `translate(${target.x}px, ${target.y}px) scale(${scale})`;
    }
  }

  #resetZoom(animate) {
    if (this.#zoom.scale === 1 && !this.#zoom.x && !this.#zoom.y) return;
    this.#zoom = { scale: 1, x: 0, y: 0 };
    this.#applyZoom(animate);
  }

  /* ---------- see in your space ---------- */

  #space = { x: 0, y: 0, ppi: 0, distance: 2, stream: null, photoUrl: '', pointers: new Map(), pinch: null };

  #bindSpace(on) {
    const dialog = this.querySelector('[data-space]');
    const open = this.querySelector('[data-open-space]');
    if (!dialog || !open) return;
    if (!this.#compose) {
      open.hidden = true;
      return;
    }
    const q = (selector) => dialog.querySelector(selector);
    const stage = q('[data-space-stage]');

    on(open, 'click', () => this.#openSpace());
    on(q('[data-space-close]'), 'click', () => {
      dialog.close();
      this.#closeSpace();
    });
    on(dialog, 'close', () => this.#closeSpace());
    on(q('[data-space-camera]'), 'click', () => this.#startCamera());
    on(q('[data-space-file]'), 'change', (event) => {
      const file = event.target.files?.[0];
      if (file) this.#showPhoto(URL.createObjectURL(file));
      event.target.value = '';
    });
    dialog.querySelectorAll('[data-space-distance]').forEach((chip) =>
      on(chip, 'click', () => {
        this.#space.distance = Number(chip.dataset.spaceDistance);
        this.#calibrateSpace();
        this.#paintSpace();
      })
    );

    // One finger moves the print, two fingers resize it to match the wall.
    const space = this.#space;
    on(stage, 'pointerdown', (event) => {
      space.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      try {
        stage.setPointerCapture(event.pointerId);
      } catch {
        // The pointer has already ended; nothing to capture.
      }
      if (space.pointers.size === 2) {
        const [a, b] = [...space.pointers.values()];
        space.pinch = { distance: Math.hypot(a.x - b.x, a.y - b.y), ppi: space.ppi };
      }
    });
    on(stage, 'pointermove', (event) => {
      const last = space.pointers.get(event.pointerId);
      if (!last) return;
      space.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (space.pointers.size === 2 && space.pinch) {
        const [a, b] = [...space.pointers.values()];
        space.ppi = clamp(space.pinch.ppi * (Math.hypot(a.x - b.x, a.y - b.y) / space.pinch.distance), 0.4, 60);
        dialog.querySelectorAll('[data-space-distance]').forEach((chip) => chip.classList.remove('is-on'));
      } else if (space.pointers.size === 1) {
        space.x += event.clientX - last.x;
        space.y += event.clientY - last.y;
      }
      this.#paintSpace();
    });
    const end = (event) => {
      space.pointers.delete(event.pointerId);
      if (space.pointers.size < 2) space.pinch = null;
    };
    on(stage, 'pointerup', end);
    on(stage, 'pointercancel', end);
    // Two fingers belong to the print, not to the page's own pinch zoom.
    on(stage, 'touchmove', (event) => event.touches.length > 1 && event.preventDefault(), { passive: false });
    ['gesturestart', 'gesturechange', 'gestureend'].forEach((type) => on(stage, type, (event) => event.preventDefault(), { passive: false }));
  }

  #openSpace() {
    const dialog = this.querySelector('[data-space]');
    const frame = this.els.frame;
    if (!dialog || !frame) return;
    const space = this.#space;
    const object = dialog.querySelector('[data-space-object]');

    // A copy of the framed print as it is chosen right now: size, finish and border.
    const copy = frame.cloneNode(true);
    copy.removeAttribute('data-frame');
    copy.querySelector('[data-mat]')?.removeAttribute('data-mat');
    copy.dataset.finish = this.#finish();
    copy.querySelector('.ps__mat')?.classList.toggle('no-trim', this.#noTrim());
    const ruler = document.createElement('div');
    ruler.className = 'ps__space-ruler ps-mono';
    ruler.dataset.spaceRuler = '';
    object.replaceChildren(copy, ruler);

    const dims = this.#dimensions();
    const choice = this.#selected.map((value, index) => this.#short(value, index)).join(' · ');
    dialog.querySelector('[data-space-label]').textContent = `${choice}${dims ? ` · ${dims.w} × ${dims.h} in` : ''}`;
    dialog.querySelector('[data-space-hint]').textContent = 'Drag to place it. Pinch to match your wall.';

    space.x = 0;
    space.y = 0;
    dialog.showModal();
    dialog.focus?.({ preventScroll: true });
    window.lenis?.stop?.();
    this.#calibrateSpace();
    this.#paintSpace();
  }

  #closeSpace() {
    const dialog = this.querySelector('[data-space]');
    this.#space.stream?.getTracks().forEach((track) => track.stop());
    this.#space.stream = null;
    this.#space.pointers.clear();
    if (this.#space.photoUrl) URL.revokeObjectURL(this.#space.photoUrl);
    this.#space.photoUrl = '';
    const video = dialog?.querySelector('[data-space-video]');
    const photo = dialog?.querySelector('[data-space-photo]');
    if (video) {
      video.srcObject = null;
      video.hidden = true;
    }
    if (photo) {
      photo.removeAttribute('src');
      photo.hidden = true;
    }
    window.lenis?.start?.();
  }

  /**
   * A phone camera sees roughly 54 degrees across the screen's width, so standing a known distance from the wall tells
   * how many real inches the screen spans. It is an estimate, which is why the print can also be resized by pinching.
   */
  #calibrateSpace() {
    const dialog = this.querySelector('[data-space]');
    const space = this.#space;
    const width = dialog.querySelector('[data-space-stage]').clientWidth;
    const inchesAcross = (2 * space.distance * Math.tan((27 * Math.PI) / 180)) / 0.0254;
    space.ppi = width / inchesAcross;
    dialog.querySelectorAll('[data-space-distance]').forEach((chip) => chip.classList.toggle('is-on', Number(chip.dataset.spaceDistance) === space.distance));
  }

  /** Sizes and places the print: its real width in inches times the scale of the picture behind it. */
  #paintSpace() {
    const dialog = this.querySelector('[data-space]');
    const object = dialog.querySelector('[data-space-object]');
    const copy = object.querySelector('.ps__frame');
    const dims = this.#dimensions();
    if (!copy || !dims) return;
    const { ppi, x, y } = this.#space;

    const outerW = dims.w * ppi;
    const edge = Math.max(1.2, ppi * 0.7);
    const mat = this.#noTrim() ? 0 : outerW * 0.07;
    const print = copy.querySelector('img');
    const artRatio = print ? (print.naturalWidth || Number(print.getAttribute('width'))) / (print.naturalHeight || Number(print.getAttribute('height'))) : 0;
    const inset = 2 * (edge + mat);
    const outerH = artRatio > 0 ? (outerW - inset) / artRatio + inset : (outerW * dims.h) / dims.w;

    object.style.width = `${outerW}px`;
    object.style.height = `${outerH}px`;
    object.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`;
    copy.style.setProperty('--ps-frame-pad', `${edge}px`);
    copy.style.setProperty('--ps-mat-pad', `${mat}px`);
    object.querySelector('[data-space-ruler]').textContent = `${dims.w} in`;
  }

  async #startCamera() {
    const dialog = this.querySelector('[data-space]');
    const hint = dialog.querySelector('[data-space-hint]');
    const video = dialog.querySelector('[data-space-video]');
    if (!navigator.mediaDevices?.getUserMedia) {
      hint.textContent = 'The camera is not available here. Upload a photo of your wall instead.';
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
      this.#space.stream?.getTracks().forEach((track) => track.stop());
      this.#space.stream = stream;
      video.srcObject = stream;
      await video.play();
      video.hidden = false;
      dialog.querySelector('[data-space-photo]').hidden = true;
      hint.textContent = 'Drag to place it. Pinch to match your wall.';
    } catch {
      hint.textContent = 'The camera could not start. Allow camera access, or upload a photo of your wall instead.';
    }
  }

  #showPhoto(url) {
    const dialog = this.querySelector('[data-space]');
    const photo = dialog.querySelector('[data-space-photo]');
    this.#space.stream?.getTracks().forEach((track) => track.stop());
    this.#space.stream = null;
    dialog.querySelector('[data-space-video]').hidden = true;
    if (this.#space.photoUrl) URL.revokeObjectURL(this.#space.photoUrl);
    this.#space.photoUrl = url;
    photo.src = url;
    photo.hidden = false;
    dialog.querySelector('[data-space-hint]').textContent = 'Drag to place it. Pinch to match your wall.';
  }

  /* ---------- size guide ---------- */

  #bindGuide(on) {
    const { guide } = this.els;
    const open = this.querySelector('[data-open-guide]');
    if (!guide || !open) return;

    on(open, 'click', () => {
      this.#renderGuide();
      guide.showModal();
    });
    this.querySelectorAll('[data-close-guide]').forEach((button) => on(button, 'click', () => guide.close()));
    on(guide, 'click', (event) => {
      if (event.target === guide) guide.close();
    });
  }

  #renderGuide() {
    const sizeIndex = this.#optionNames.findIndex((name) => name.toLowerCase() === 'size');
    if (sizeIndex < 0) return;

    const seen = new Map();
    this.#variants.forEach((variant) => {
      const value = variant.options[sizeIndex];
      const match = value.match(DIMENSIONS);
      if (match && !seen.has(value)) seen.set(value, { value, w: Number(match[1]), h: Number(match[2]) });
    });
    const sizes = [...seen.values()].sort((a, b) => a.w * a.h - b.w * b.h);
    if (!sizes.length) return;

    const src = this.els.art.querySelector('img')?.currentSrc ?? '';
    const tallest = Math.max(...sizes.map((size) => size.h));
    const pxPerInch = 190 / tallest;
    const finish = this.#finish();
    const frameColor = { black: '#1c1c1b', white: '#f8f7f3', brown: '#805337' }[finish];
    const noTrim = this.#noTrim();

    const cards = sizes.map((size) => {
      // The price for this size with the frame and border already chosen.
      const selection = [...this.#selected];
      selection[sizeIndex] = size.value;
      const variant = this.#findVariant(selection);

      const width = size.w * pxPerInch;
      const height = size.h * pxPerInch;
      const border = clamp(width * 0.045, 3, 8);
      const mat = noTrim ? 0 : clamp(width * 0.08, 4, 14);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ps__guide-size';
      button.setAttribute('aria-pressed', String(size.value === this.#selected[sizeIndex]));

      const frame = document.createElement('span');
      frame.className = 'ps__guide-frame';
      frame.style.cssText = `width:${width}px;height:${height}px;border:${border}px solid ${frameColor};padding:${mat}px;${
        finish === 'white' ? 'outline:1px solid #7a787050;' : ''
      }`;
      const img = document.createElement('img');
      img.alt = '';
      img.src = src;
      frame.append(img);

      const [name, detail] = size.value.split(' (');
      const meta = document.createElement('span');
      meta.className = 'ps__guide-meta ps-mono';
      const title = document.createElement('strong');
      title.textContent = name;
      const dims = document.createElement('span');
      dims.textContent = `${size.w} × ${size.h} in`;
      meta.append(title, dims);
      if (variant) {
        const price = document.createElement('span');
        price.textContent = variant.price;
        meta.append(price);
      }
      if (detail) button.setAttribute('aria-label', `${name}, ${detail.replace(')', '')}`);

      button.addEventListener('click', () => {
        this.#selected[sizeIndex] = size.value;
        this.#sync(true);
        this.els.guide.close();
      });
      button.append(frame, meta);
      return button;
    });

    this.querySelector('[data-guide-sizes]').replaceChildren(...cards);
  }

  /* ---------- cart ---------- */

  async #addToBag() {
    const variant = this.#findVariant();
    if (!variant?.available || this.#adding) return;

    this.#adding = true;
    this.els.add.disabled = true;
    this.els.error.hidden = true;

    const body = new FormData(this.els.form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((item) => item.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) body.append('sections', sectionIds.join(','));

    try {
      const config = fetchConfig('javascript', { body });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const json = await response.json();

      if (json.status) {
        this.els.error.textContent = json.description || json.message || 'Could not add to bag';
        this.els.error.hidden = false;
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, String(variant.id), {
          source: 'product-form-component',
          itemCount: 1,
          productId: this.dataset.productId,
          sections: json.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.els.error.textContent = 'Something went wrong. Please try again.';
      this.els.error.hidden = false;
    } finally {
      this.#adding = false;
      this.els.add.disabled = !variant.available;
    }
  }
}

if (!customElements.get('product-studio-component')) {
  customElements.define('product-studio-component', ProductStudioComponent);
}
